// Sub-loop transcript surface. Phase 11 §Where: the parent renders a
// collapsible boundary; expanding it reveals the nested events emitted by
// the sub-session.

import { type ReactNode } from "react";

import { useT } from "../../i18n/index.js";
import { useRemountSafeState } from "../../state/remount-safe-state";

interface RoleSpawnBoundaryProps {
  readonly role: string;
  readonly task: string;
  readonly summary?: string;
  /**
   * Pre-rendered child transcript — the parent threads forwarded sub-loop
   * events into this slot. The boundary is layout-only.
   */
  readonly children?: ReactNode;
  /** Default open state. Tests use `defaultOpen` to render the expanded view. */
  readonly defaultOpen?: boolean;
  /** Remount-safe key for the disclosure toggle (the entry's toolUseId), so a
   *  transcript the user opened doesn't snap shut on the reconnect replay. */
  readonly stateKey?: string;
}

export function RoleSpawnBoundary({
  role,
  task,
  summary,
  children,
  defaultOpen = false,
  stateKey,
}: RoleSpawnBoundaryProps) {
  const t = useT();
  const [open, setOpen] = useRemountSafeState(stateKey ?? null, defaultOpen);
  const hasTranscript = children !== undefined;
  return (
    <div className="rounded-md border border-role-line bg-role-soft p-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-role-fg">
          {t("chatRender.subAgent", { role })}
        </span>
        {hasTranscript && (
          <span className="text-[10px] font-semibold text-role-fg">
            {open ? t("chatRender.hideTranscript") : t("chatRender.showTranscript")}
          </span>
        )}
      </button>
      <div className="mt-1 text-xs text-role-fg-3">{task}</div>
      {open && hasTranscript && (
        <div className="mt-2 border-t border-role-line pt-2">{children}</div>
      )}
      {summary !== undefined && (
        <div className="mt-2 border-t border-role-line pt-2 text-xs text-role-fg-3">
          <span className="font-semibold">{t("chatRender.returned")}</span> {summary}
        </div>
      )}
    </div>
  );
}
